import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Users, Briefcase, Code, Database, Tv, Palette, Calendar, Monitor, Wrench, Languages, MapPin, GraduationCap, Zap } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { projectsData } from '../data/projectsData';
import MinimalProjectCard from '../components/MinimalProjectCard';
import '../styles/About.css';

const About: React.FC = () => {
  const [mainContent, setMainContent] = useState<string>('');
  const [sidebarContent, setSidebarContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const [mainRes, sidebarRes] = await Promise.all([
          fetch('/content/about/cv/main.md'),
          fetch('/content/about/cv/sidebar.md')
        ]);

        const [mainText, sidebarText] = await Promise.all([
          mainRes.text(),
          sidebarRes.text()
        ]);

        console.log('📄 CV Main content loaded:', mainText.substring(0, 200) + '...');
        console.log('📄 CV content length:', mainText.length);
        
        setMainContent(mainText);
        setSidebarContent(sidebarText);
        setLoading(false);
      } catch (error) {
        console.error('Error loading about content:', error);
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      // Consider scrolled when user has scrolled more than 100px
      setIsScrolled(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Parse sidebar content into structured data
  const parseSkills = (content: string) => {
    const sections: { [key: string]: string[] } = {};
    const lines = content.split('\n');
    let currentSection = '';
    
    lines.forEach(line => {
      if (line.startsWith('### ')) {
        currentSection = line.replace('### ', '').toLowerCase();
        sections[currentSection] = [];
      } else if (line.startsWith('- ') && currentSection) {
        sections[currentSection].push(line.replace('- ', ''));
      }
    });
    
    return sections;
  };

  // Logo mappings for skills
  const skillLogos: { [key: string]: string } = {
    // Development
    'javascript': '/images/skills/logos/javascript.svg',
    'c#': '/images/skills/logos/csharp.svg',
    'nodejs': '/images/skills/logos/nodejs.svg',
    'react': '/images/skills/logos/react.svg',
    
    // Databases
    'mongo': '/images/skills/logos/mongodb.svg',
    'couch': '/images/skills/logos/couchdb.svg',
    'sql': '/images/skills/logos/sql.svg',
    'postgresql': '/images/skills/logos/postgresql.svg',
    
    // Broadcast
    'mosart': '/images/skills/logos/mosart.svg',
    'newsroom': '/images/skills/logos/newsroom.svg',
    'mam': '/images/skills/logos/mam.svg',
    'videow-wall': '/images/skills/logos/videowall.svg',
    
    // Vizrt
    'artist': '/images/skills/logos/vizrt-artist.svg',
    'pilot': '/images/skills/logos/vizrt-pilot.svg',
    'viz one': '/images/skills/logos/viz-one.svg',
    'ar/vr': '/images/skills/logos/arvr.svg',
    'datapool': '/images/skills/logos/datapool.svg',
    
    // Languages (using flag emojis as fallback)
    'spanish': '🇪🇸',
    'english': '🇬🇧',
    'german': '🇩🇪',
  };


  const extractKeyProjects = (content: string) => {
    const lines = content.split('\n');
    console.log('Extracting projects from lines:', lines.length);
    
    // Debug: Show all lines that contain "Key" or "projects"
    lines.forEach((line, i) => {
      if (line.includes('Key') || line.includes('projects')) {
        console.log(`Line ${i}: "${line}" (length: ${line.length})`);
      }
    });
    
    for (let i = 0; i < lines.length; i++) {
      const currentLine = lines[i].trim(); // Add trim here to handle whitespace
      
      if (currentLine === '### Key Projects' || currentLine === '## Key Projects') {
        console.log(`Found Key Projects header at line ${i}: "${currentLine}"`);
        i++; // Skip the header
        
        if (i < lines.length) {
          const nextLine = lines[i].trim(); // Add trim here too
          console.log(`Next line ${i}: "${nextLine}"`);
          
          if (nextLine.startsWith('projects: ')) {
            const projectIdsString = nextLine.replace('projects: ', '').trim();
            console.log(`Project IDs string: "${projectIdsString}"`);
            
            if (projectIdsString) {
              const projectIds = projectIdsString.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
              console.log('Parsed project IDs:', projectIds);
              
              const projects = projectIds.map(id => {
                const project = projectsData.find(p => p.id === id);
                return project;
              }).filter((project): project is NonNullable<typeof project> => project !== undefined);
              
              console.log('Found projects:', projects.map(p => p.title));
              return projects;
            }
          }
        }
        break;
      }
    }
    
    return [];
  };

  if (loading) {
    return (
      <div className="about">
        <div className="loading-state">
          <p>Loading CV content...</p>
        </div>
      </div>
    );
  }

  const skillSections = parseSkills(sidebarContent);

  return (
    <>
      <Helmet>
        <title>About Pedro Sugasti - Broadcast Specialist & Creative Developer</title>
        <meta name="description" content="Learn about Pedro Sugasti's background as a broadcast specialist from Argentina living in Berlin. Over a decade of experience in computer graphics, broadcasting, and creative development." />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="About Pedro Sugasti - Broadcast Specialist" />
        <meta property="og:description" content="Broadcast specialist from Argentina living in Berlin with over a decade of experience in computer graphics and broadcasting. Technical expertise combined with creative vision." />
        <meta property="og:image" content="https://pedrosugasti.com/profile-photo.jpg" />
        <meta property="og:url" content="https://pedrosugasti.com/about" />
        <meta property="og:type" content="profile" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Pedro Sugasti" />
        <meta name="twitter:description" content="Broadcast specialist from Argentina living in Berlin. Over a decade of experience in computer graphics and broadcasting." />
        <meta name="twitter:image" content="https://pedrosugasti.com/profile-photo.jpg" />
        
        <link rel="canonical" href="https://pedrosugasti.com/about" />
      </Helmet>
      
      <div className="about">
      {/* Hero Section */}
      <section className={`about-hero ${isScrolled ? 'scrolled' : ''}`}>
        <div className="hero-content">
          <User size={24} className="about-hero-icon" />
          <div className="about-hero-text">
            <h1>About</h1>
            <p className="lead">Professional background and experience</p>
          </div>
        </div>
      </section>

      <section className="about-content">
        {/* Professional Summary */}
        <div className="professional-summary">
          <h2><User size={20} /> Summary</h2>
          <p>Broadcast graphics specialist and software developer with 20+ years of experience in software development, 
            real-time graphics, data visualization, and broadcast systems. Based in Berlin with roots in Argentina, 
            I have worked with leading broadcasters such as SKY and WELT, and supported international 
            networks including CNN, FOX, ESPN and CBS. Skilled in bridging creativity and technology, 
            I focus on delivering innovative, reliable solutions for live production and broadcast environments.</p>
        </div>

        {/* Professional Experience */}
        <div className="experience-section">
          <h2><Briefcase size={20} /> Experience</h2>
          <div className="cv-content">
            {mainContent.split(/(?=^# )/gm).filter(section => section.trim()).map((section, index) => {
              const lines = section.trim().split('\n');
              const companyMatch = lines[0].match(/^# (.+)/);
              const dateMatch = lines[1]?.match(/^## (.+)/);
              const positionMatch = lines.find(line => line.match(/^\*\*(.+)\*\*$/))?.match(/^\*\*(.+)\*\*$/);
              
              if (!companyMatch) return null;
              
              const company = companyMatch[1];
              const fullDate = dateMatch ? dateMatch[1] : '';
              const position = positionMatch ? positionMatch[1] : '';
              
              // Extract date and location from "Dec 2021 to Present (Berlin)"
              const locationMatch = fullDate.match(/\(([^)]+)\)/);
              const location = locationMatch ? locationMatch[1] : '';
              const dateOnly = fullDate.replace(/\s*\([^)]+\)/, '').trim();
              
              // Get content after the position line - this includes responsibilities and Key Projects
              const positionLineIndex = lines.findIndex(line => line.match(/^\*\*(.+)\*\*$/));
              const contentLines = positionLineIndex !== -1 ? lines.slice(positionLineIndex + 1) : lines.slice(2);
              const content = contentLines.join('\n');
              
              // Also get the full section content for Key Projects parsing (includes the full section)
              const fullSectionContent = section;
              
              // Separate content into job description and responsibilities  
              const jobDescriptionParts = [];
              const responsibilitiesParts = [];
              let isInList = false;
              
              const contentParts = content.split('\n');
              for (const line of contentParts) {
                // Skip project ID lines and Key Projects headers
                if (line.startsWith('projects:') || line === '### Key Projects' || line === '## Key Projects') {
                  continue;
                }
                
                // Check if we hit a bullet list
                if (line.startsWith('- ')) {
                  isInList = true;
                }
                
                if (isInList) {
                  responsibilitiesParts.push(line);
                } else if (line.trim() !== '') {
                  jobDescriptionParts.push(line);
                }
              }
              
              const jobDescriptionText = jobDescriptionParts.join('\n');
              const responsibilitiesText = responsibilitiesParts.join('\n');
              const keyProjects = extractKeyProjects(fullSectionContent);
              
              // Debug: Check if projects are found
              console.log(`✅ ${company}: Found ${keyProjects.length} projects`, keyProjects.map(p => p.title));

              return (
                <div key={index} className="company-section">
                  {/* Row 1: Job Header with company/description left, position/date right */}
                  <div className="job-header-layout">
                    <div className="job-header-left">
                      <h3 className="company-name">{company}</h3>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p className="job-description">{children}</p>,
                          ul: () => null, // Hide lists in header
                          li: () => null, // Hide list items in header
                          h2: () => null, // Hide h2 in header
                          h3: () => null  // Hide h3 in header
                        }}
                      >
                        {jobDescriptionText}
                      </ReactMarkdown>
                    </div>
                    <div className="job-header-right">
                      <div className="position-title">{position}</div>
                      <div className="position-meta">
                        <div className="position-period">
                          <Calendar size={16} />
                          {dateOnly}
                        </div>
                        {location && (
                          <div className="position-location">
                            <MapPin size={16} />
                            {location}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Row 2: Responsibilities and Projects */}
                  <div className="job-responsibilities">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: () => null, // Hide paragraphs in responsibilities
                        ul: ({ children }) => <ul className="responsibilities-list">{children}</ul>,
                        li: ({ children }) => <li className="responsibility-item">{children}</li>,
                        h2: () => null, // Hide h2 in responsibilities
                        h3: () => null  // Hide h3 in responsibilities
                      }}
                    >
                      {responsibilitiesText}
                    </ReactMarkdown>
                    
                    {/* Key Projects using ProjectCard components */}
                    {keyProjects.length > 0 && (
                      <div className="job-key-projects">
                        <div className="job-projects-header">
                          <h4 className="projects-title">
                            <Zap size={16} />
                            Key Projects
                          </h4>
                        </div>
                        <div className="job-projects-grid">
                          {keyProjects.map((project) => (
                            <MinimalProjectCard key={project.id} project={project} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Skills Grid */}
        <div className="skills-comprehensive">
          <h2><Code size={20} /> Technical Expertise</h2>
          <div className="skills-master-grid">
            {Object.entries(skillSections).map(([category, skills]) => {
              let icon = Code;
              const categoryLower = category.toLowerCase();
              if (categoryLower.includes('development')) icon = Code;
              else if (categoryLower.includes('database')) icon = Database;
              else if (categoryLower.includes('broadcast')) icon = Tv;
              else if (categoryLower.includes('graphics')) icon = Palette;
              else if (categoryLower.includes('academic')) icon = GraduationCap;
              else if (categoryLower.includes('language')) icon = Languages;
              else if (categoryLower.includes('vizrt')) icon = Monitor;
              else if (categoryLower.includes('shows')) icon = Calendar;
              else icon = Wrench;

              return (
                <div key={category} className="skill-master-category">
                  <h3 className="skill-master-title">
                    {React.createElement(icon, { size: 20 })}
                    {category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </h3>
                  <div className="skill-master-list">
                    {skills.map((skill, index) => {
                      // Handle language skills with proficiency levels (e.g., "Spanish: Native")
                      const skillName = skill.includes(':') ? skill.split(':')[0].trim() : skill;
                      const skillKey = skillName.toLowerCase();
                      const logo = skillLogos[skillKey];
                      const isEmoji = logo && logo.startsWith('🇪');
                      
                      return (
                        <div key={index} className="skill-master-item">
                          {logo && (
                            isEmoji ? (
                              <span className="skill-flag-emoji">{logo}</span>
                            ) : (
                              <img 
                                src={logo} 
                                alt={skillName} 
                                className="skill-master-logo"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            )
                          )}
                          <span className="skill-master-name">{skill}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>


        {/* Contact Information */}
        <div className="contact-cta">
          <h2><Users size={20} /> Let's Collaborate</h2>
          <p>
            Ready to bring innovative broadcast graphics and technical solutions to your next project. 
            Available for consulting, full-time opportunities, and freelance projects.
          </p>
          <div className="contact-actions">
            <Link to="/contact" className="btn btn-primary">
              Get In Touch
            </Link>
            <Link to="/work" className="btn btn-secondary">
              View Portfolio
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default About;